import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";

const CartContext = createContext(null);

/* =========================
   PRICE LOGIC (SOURCE OF TRUTH)
========================= */
const getPrice = (product) => {
  const hasDiscount =
    Number(product.discount_percentage) > 0 &&
    Number(product.final_price) > 0 &&
    Number(product.final_price) < Number(product.original_price);

  return hasDiscount
    ? Number(product.final_price)
    : Number(product.original_price);
};

export function CartProvider({ children }) {
  /* =========================
     LOAD CART FROM LOCALSTORAGE
  ========================= */
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem("luma3d_cart");
    if (!saved) return [];

    // Carts saved before color support existed have no colorId field at
    // all (undefined) - normalize to null so every comparison below can
    // assume colorId is always either a number or null, never undefined.
    return JSON.parse(saved).map((item) => ({
      ...item,
      colorId: item.colorId ?? null,
    }));
  });

  const [drawerOpen, setDrawerOpen] = useState(false);

  const openCart = useCallback(() => setDrawerOpen(true), []);
  const closeCart = useCallback(() => setDrawerOpen(false), []);
  const toggleCart = useCallback(() => setDrawerOpen((v) => !v), []);

  /* =========================
     SAVE CART TO LOCALSTORAGE
  ========================= */
  useEffect(() => {
    localStorage.setItem("luma3d_cart", JSON.stringify(items));
  }, [items]);

  /* =========================
     ADD TO CART (FIXED PRICE LOGIC)
  ========================= */
  // A product can now be added in different colors, each of which needs its
  // own cart line (separate quantity, separate removal) rather than being
  // merged into one - colorId is part of what identifies "the same line",
  // not just the product id, everywhere below.
  const addToCart = useCallback((product, qty = 1, color = null) => {
    setItems((prev) => {
      const colorId = color?.id ?? null;
      const existing = prev.find((x) => x.id === product.id && x.colorId === colorId);

      const price = getPrice(product);

      if (existing) {
        return prev.map((x) =>
          x.id === product.id && x.colorId === colorId
            ? { ...x, qty: x.qty + qty }
            : x
        );
      }

      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          image: product.image,
          finish: product.finish,

          colorId,
          colorName: color?.name ?? null,
          colorHex: color?.hex_value ?? null,

          // 🔥 IMPORTANT: unified price
          price,

          // optional (for UI badges)
          discount_percentage: product.discount_percentage,
          final_price: product.final_price,
          original_price: product.original_price,
          customized: product.customized,

          qty,
        },
      ];
    });
  }, []);

  /* =========================
     UPDATE QUANTITY
  ========================= */
  const updateQty = useCallback((id, qty, colorId = null) => {
    setItems((prev) =>
      prev
        .map((x) =>
          x.id === id && x.colorId === colorId ? { ...x, qty: Math.max(1, qty) } : x
        )
        .filter((x) => x.qty > 0)
    );
  }, []);

  /* =========================
     REMOVE ITEM
  ========================= */
  const removeItem = useCallback(
    (id, colorId = null) =>
      setItems((prev) => prev.filter((x) => !(x.id === id && x.colorId === colorId))),
    []
  );

  /* =========================
     CLEAR CART
  ========================= */
  const clearCart = useCallback(() => setItems([]), []);

  /* =========================
     TOTALS (ALWAYS RELIABLE)
  ========================= */
  const subtotal = useMemo(
    () => items.reduce((sum, x) => sum + x.price * x.qty, 0),
    [items]
  );

  const count = useMemo(
    () => items.reduce((sum, x) => sum + x.qty, 0),
    [items]
  );

  /* =========================
     PROVIDER
  ========================= */
  const value = useMemo(
    () => ({
      items,
      addToCart,
      updateQty,
      removeItem,
      clearCart,

      subtotal,
      count,

      drawerOpen,
      openCart,
      closeCart,
      toggleCart,
    }),
    [items, addToCart, updateQty, removeItem, clearCart, subtotal, count, drawerOpen, openCart, closeCart, toggleCart]
  );

  return (
    <CartContext.Provider value={value}>{children}</CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);